import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Globe as Chrome, Maximize2, PauseCircle, AlertTriangle, ShieldCheck, MonitorSmartphone } from "lucide-react";

export function browserSupport() {
  const ua = navigator.userAgent || "";
  const mobile = navigator.userAgentData?.mobile ?? /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
  const brands = (navigator.userAgentData?.brands || []).map((b) => b.brand).join(" ");
  const chromium = /Google Chrome|Microsoft Edge/.test(brands) || (/Chrome\/|Edg\//.test(ua) && !/OPR\/|SamsungBrowser|Firefox|FxiOS|CriOS|EdgiOS/.test(ua));
  return { ok: chromium && !mobile, mobile };
}

export function GateShell({ testId, children }) {
  return (
    <div className="dark fixed inset-0 z-[60] bg-[#070809] overflow-y-auto text-foreground flex items-center justify-center p-6" data-testid={testId}>
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-80 w-80 rounded-full bg-emerald-500/15 blur-[120px]" aria-hidden />
      <motion.div initial={{ opacity: 0, y: 18, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ type: "spring", stiffness: 220, damping: 24 }}
        className="relative max-w-md w-full rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-7 text-center space-y-4">
        {children}
      </motion.div>
    </div>
  );
}

export function BrowserBlockGate({ mobile, onExit }) {
  return (
    <GateShell testId="lockdown-browser-gate">
      <div className="mx-auto h-12 w-12 rounded-xl bg-amber-500/15 flex items-center justify-center"><MonitorSmartphone className="h-6 w-6 text-amber-400" /></div>
      <h1 className="text-2xl font-display font-bold">Please switch browser</h1>
      <p className="text-sm text-muted-foreground leading-relaxed">
        This test runs in lockdown mode, which needs {mobile ? "a desktop or laptop computer with " : ""}Google Chrome or Microsoft Edge. Open your test link there to begin. Your timer hasn't started.
      </p>
      <div className="grid grid-cols-2 gap-2.5">
        <Button variant="outline" asChild><a href="https://www.google.com/chrome/" target="_blank" rel="noopener noreferrer" data-testid="get-chrome"><Chrome className="h-4 w-4 mr-1.5" /> Get Chrome</a></Button>
        <Button variant="outline" asChild><a href="https://www.microsoft.com/edge/download" target="_blank" rel="noopener noreferrer" data-testid="get-edge">Get Edge</a></Button>
      </div>
      <button onClick={onExit} className="text-xs text-muted-foreground hover:text-foreground" data-testid="lockdown-browser-exit">Exit</button>
    </GateShell>
  );
}

const RULES = ["The test stays in full screen", "Copy, paste, right-click and printing are turned off", "Leaving the tab or full screen is noted for the reviewer", "A second monitor is checked for"];

export function FullscreenGate({ returning, onEnter, onExit }) {
  return (
    <GateShell testId={returning ? "lockdown-return-gate" : "lockdown-start-gate"}>
      <div className="mx-auto h-12 w-12 rounded-xl bg-primary/15 flex items-center justify-center"><Maximize2 className="h-6 w-6 text-primary" /></div>
      <h1 className="text-2xl font-display font-bold">{returning ? "Return to full screen" : "Lockdown mode"}</h1>
      {returning ? (
        <p className="text-sm text-muted-foreground">You left full screen. This has been noted for the reviewer. Go back to full screen to carry on with your test.</p>
      ) : (
        <ul className="text-left text-sm text-muted-foreground space-y-2" data-testid="lockdown-rules">
          {RULES.map((r, i) => (
            <motion.li key={r} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + i * 0.07 }} className="flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 text-primary shrink-0 mt-0.5" /> {r}
            </motion.li>
          ))}
        </ul>
      )}
      <Button className="w-full gap-1.5" onClick={onEnter} data-testid={returning ? "lockdown-return-btn" : "lockdown-start-btn"}><Maximize2 className="h-4 w-4" /> {returning ? "Back to full screen" : "Start in full screen"}</Button>
      {!returning && <button onClick={onExit} className="text-xs text-muted-foreground hover:text-foreground" data-testid="lockdown-start-exit">Not now</button>}
    </GateShell>
  );
}

export function PausedOverlay() {
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-md p-6" data-testid="proctor-paused-overlay">
      <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} className="max-w-md rounded-2xl border border-amber-400/30 bg-card p-7 text-center space-y-3">
        <motion.div animate={{ scale: [1, 1.08, 1] }} transition={{ repeat: Infinity, duration: 2.4 }} className="mx-auto h-12 w-12 rounded-full bg-amber-500/15 flex items-center justify-center"><PauseCircle className="h-6 w-6 text-amber-400" /></motion.div>
        <h2 className="font-display text-xl font-semibold">Your test is paused</h2>
        <p className="text-sm text-muted-foreground">A few monitoring checks need a recruiter's look. Your answers are saved and <strong>your timer is stopped</strong>. Please stay on this page; you can carry on as soon as they let you continue.</p>
      </motion.div>
    </div>
  );
}

export function RuleWarning({ state, message }) {
  if (!state?.warnings) return null;
  const end = state.action === "end", pause = state.action === "pause";
  return (
    <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 flex items-start gap-2.5" data-testid="proctor-warning">
      <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
      <p className="text-xs text-amber-100/90 leading-relaxed">
        {message ? `${message} ` : ""}Warning <strong data-testid="proctor-warning-count">{state.warnings}</strong>{state.limit ? ` of ${state.limit}` : ""}.
        {state.limit && (end || pause) ? ` At ${state.limit}, the test will ${end ? "end and your answers will be submitted" : "pause until a recruiter checks in"}.` : " A person reviews every flag."}
      </p>
    </motion.div>
  );
}
